import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { apiCallWithRetry } from '../utils/apiRetry';

// Global cache for credit data to prevent multiple fetches
let globalCreditCache = {
  data: null,
  timestamp: null,
  isLoading: false
};

const CACHE_DURATION = 30000; // 30 seconds

export default function MyCreditStatus({ creditPercentage, showPercentage = true }) {
  const { isAuthenticated, authToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [creditData, setCreditData] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const mountedRef = useRef(true);

  // Helper function to handle API errors
  const handleError = (err) => {
    if (err.name === 'APIError') {
      if (err.status === 524) {
        return 'Request timed out. Please try again.';
      }
      return err.message || 'Failed to load credits';
    }
    return err.message || 'Failed to load credits';
  };

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    console.log('MyCreditStatus useEffect:', { isAuthenticated, showPercentage, creditPercentage })
    if (!isAuthenticated || !authToken) {
      console.log('MyCreditStatus: User not authenticated, skipping credit fetch')
      setLoading(false);
      // Clear cache when logged out
      globalCreditCache = { data: null, timestamp: null, isLoading: false };
      return;
    }

    // Check if we have valid cached data
    const now = Date.now();
    if (globalCreditCache.data && globalCreditCache.timestamp && 
        (now - globalCreditCache.timestamp) < CACHE_DURATION) {
      console.log('MyCreditStatus: Using cached data')
      setCreditData(globalCreditCache.data);
      setLoading(false);
      return;
    }

    // If another instance is already loading, wait for it
    if (globalCreditCache.isLoading) {
      console.log('MyCreditStatus: Another instance is already loading')
      const checkInterval = setInterval(() => {
        if (!globalCreditCache.isLoading && mountedRef.current) {
          clearInterval(checkInterval);
          if (globalCreditCache.data) {
            setCreditData(globalCreditCache.data);
            setLoading(false);
          }
        }
      }, 100);
      return () => clearInterval(checkInterval);
    }

    console.log('MyCreditStatus: Fetching credit data for authenticated user')
    globalCreditCache.isLoading = true;
    
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch credit balance and subscription data with retry logic
        const data = await apiCallWithRetry(
          '/api/credits/balance/',
          { method: 'GET' },
          authToken,
          {
            maxRetries: 3,
            onRetry: (attempt, delay) => {
              console.log(`Retrying credit fetch (attempt ${attempt})...`);
              if (mountedRef.current) {
                setRetryCount(attempt);
              }
            }
          }
        );
        
        // Update global cache
        globalCreditCache = {
          data: data,
          timestamp: Date.now(),
          isLoading: false
        };
        
        if (mountedRef.current) {
          setCreditData(data);
          setRetryCount(0);
        }
      } catch (err) {
        console.error('Failed to fetch credit data:', err);
        globalCreditCache.isLoading = false;
        if (mountedRef.current) {
          const errorMessage = handleError(err);
          setError(errorMessage);
        }
      } finally {
        if (mountedRef.current) {
          setLoading(false);
        }
      }
    };
    
    fetchData();
  }, [isAuthenticated, authToken]);

  // Define fetchCreditData for retry button
  const fetchCreditData = async () => {
    try {
      setLoading(true);
      setError(null);
      // Clear cache to force refresh
      globalCreditCache = { data: null, timestamp: null, isLoading: true };
      
      // Fetch credit balance and subscription data with retry logic
      const data = await apiCallWithRetry(
        '/api/credits/balance/',
        { method: 'GET' },
        authToken,
        {
          maxRetries: 3,
          onRetry: (attempt, delay) => {
            console.log(`Retrying credit fetch (attempt ${attempt})...`);
            setRetryCount(attempt);
          }
        }
      );
      
      // Update global cache
      globalCreditCache = {
        data: data,
        timestamp: Date.now(),
        isLoading: false
      };
      
      setCreditData(data);
      setRetryCount(0);
    } catch (err) {
      console.error('Failed to fetch credit data:', err);
      globalCreditCache.isLoading = false;
      const errorMessage = handleError(err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return null;
  }

  if (loading) {
    return (
      <div className="flex items-center space-x-2">
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
        <span className="text-sm text-gray-600">
          {retryCount > 0 ? `Loading... (retry ${retryCount})` : 'Loading...'}
        </span>
      </div>
    );
  }

  if (error || !creditData) {
    return (
      <div className="flex flex-col space-y-1">
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 bg-red-500 rounded-full"></div>
          <span className="text-sm text-red-600">{error || 'Error loading credits'}</span>
        </div>
        <button 
          onClick={fetchCreditData}
          className="text-xs text-blue-600 hover:text-blue-800 underline"
        >
          Retry
        </button>
      </div>
    );
  }

  const { balance, subscription } = creditData;

  // Calculate percentage if we have subscription data
  const calculatedPercentage = subscription?.plan?.monthly_credits 
    ? Math.round((balance / subscription.plan.monthly_credits) * 100)
    : creditPercentage;

  // Use provided creditPercentage or calculated percentage
  const displayPercentage = creditPercentage !== undefined ? creditPercentage : calculatedPercentage;

  // Determine if credits are low (less than 10% of monthly allocation or less than 10)
  const isLowCredits = balance < 10 || (subscription?.plan?.monthly_credits && balance < subscription.plan.monthly_credits * 0.1);

  // Get subscription status styling
  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'text-green-600';
      case 'canceled':
        return 'text-red-600';
      case 'past_due':
        return 'text-yellow-600';
      default:
        return 'text-gray-600';
    }
  };

  // Compact sidebar view when showPercentage is false
  if (!showPercentage) {
    return (
      <div className="flex items-center">
        <div className="relative w-8 h-8">
          {/* Credit usage circle */}
          <svg className="w-8 h-8 transform -rotate-90" viewBox="0 0 36 36">
            <path
              d="M18 2.0845
                 a 15.9155 15.9155 0 0 1 0 31.831
                 a 15.9155 15.9155 0 0 1 0 -31.831"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeDasharray={`${displayPercentage || 0}, 100`}
              className={displayPercentage < 20 ? "text-red-500" : displayPercentage < 50 ? "text-yellow-500" : "text-green-500"}
            />
            <path
              d="M18 2.0845
                 a 15.9155 15.9155 0 0 1 0 31.831
                 a 15.9155 15.9155 0 0 1 0 -31.831"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeDasharray="100, 100"
              className="text-gray-200 dark:text-gray-700"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
              {displayPercentage || 0}%
            </span>
          </div>
        </div>
      </div>
    );
  }

  // Full component view for modal/popup
  return (
    <div className="bg-white dark:bg-dark-card rounded-lg border border-gray-200 dark:border-dark-border p-4 space-y-3">
      {/* Credit Balance */}
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Credits</span>
        <div className={`flex items-center space-x-2 ${isLowCredits ? 'text-red-600 font-semibold' : 'text-gray-900 dark:text-gray-100'}`}>
          {isLowCredits && (
            <svg className="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          )}
          <span className={isLowCredits ? 'warning low red' : ''}>{balance}</span>
        </div>
      </div>

      {/* Credit Usage Percentage */}
      {displayPercentage !== undefined && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600 dark:text-gray-400">Usage</span>
          <span className="text-sm font-medium">{displayPercentage}%</span>
        </div>
      )}

      {/* Subscription Information */}
      <div className="border-t border-gray-200 dark:border-dark-border pt-3">
        {subscription ? (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Plan</span>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{subscription.plan.name}</span>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Monthly Credits</span>
              <span className="text-sm text-gray-900 dark:text-gray-100">{subscription.plan.credits_included || subscription.plan.monthly_credits}</span>
            </div>
            
            {subscription.billing_cycle && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Billing</span>
                <span className="text-sm capitalize text-gray-900 dark:text-gray-100">{subscription.billing_cycle}</span>
              </div>
            )}
            
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Status</span>
              <span className={`text-sm font-medium capitalize ${getStatusColor(subscription.status)}`}>
                {subscription.status}
              </span>
            </div>
          </div>
        ) : (
          <div className="text-center">
            <span className="text-sm text-gray-600 dark:text-gray-400">Free Plan</span>
            <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
              Upgrade for more credits
            </div>
          </div>
        )}
      </div>

      {/* Low Credit Warning */}
      {isLowCredits && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-2">
          <p className="text-xs text-red-600 dark:text-red-400">
            Low credits remaining. Consider purchasing more credits or upgrading your plan.
          </p>
        </div>
      )}
    </div>
  );
}
